import { api } from "@/utils/api";
import { socketService } from "@/utils/socket";
import { WSEvent } from "@shared/constants";
import type { LoginResponse, SafeUser, WSEventPayload } from "@shared/types";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useRolesStore } from "./roles";

export const useAuthStore = defineStore("auth", () => {
  // State
  const user = ref<SafeUser | null>(null);
  const token = ref<string | null>(localStorage.getItem("token"));
  const isLoading = ref(false);
  const isInitializing = ref(!!token.value);

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value);
  const isAdmin = computed(() => user.value?.role_id === 1);

  /**
   * Check if user can access admin features (has any .view or .edit admin permissions)
   */
  const canAccessAdmin = computed(() => {
    if (!user.value) return false;

    // Admin role always has access
    if (user.value.role_id === 1) return true;

    // Wait for roles to load before checking permissions
    const rolesStore = useRolesStore();
    if (!rolesStore.roles || rolesStore.roles.length === 0) return false;

    // Check if user has any admin-related permissions (viewAll or editAll)
    const hasAccess = hasAnyPermission([
      // User Management
      "users.viewAll",
      "users.editAll",
      "roles.viewAll",
      "roles.editAll",

      // Mappings
      "mappings.viewAll",
      "mappings.editAll",
      "variants.viewAll",
      "variants.editAll",

      // Scraping
      "roundConfigs.viewAll",
      "roundConfigs.editAll",
      "scraperConfigs.viewAll",
      "scraperConfigs.editAll",

      // Proxies & Profiles
      "proxies.viewAll",
      "proxies.editAll",
      "browserProfiles.viewAll",
      "browserProfiles.editAll",
      "fingerprints.viewAll",
      "fingerprints.editAll",

      // Analytics
      "analytics.viewAll",
      "analytics.editAll",

      // Settings & Logs
      "systemSettings.viewAll",
      "systemSettings.editAll",
      "logs.viewAll",
      "logs.editAll",
      "audit.viewAll",
      "audit.editAll",
    ]);

    return hasAccess;
  });

  /**
   * Check if user has specific permission
   */
  const hasPermission = (permissionName: string): boolean => {
    if (!user.value) return false;

    const rolesStore = useRolesStore();
    const userRole = rolesStore.getRoleById(user.value.role_id);
    if (!userRole) return false;

    return userRole.permissions.some(
      (p: { name: string }) => p.name === permissionName,
    );
  };

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = (permissionNames: string[]): boolean => {
    if (!user.value) return false;

    const rolesStore = useRolesStore();
    const userRole = rolesStore.getRoleById(user.value.role_id);
    if (!userRole) return false;

    return permissionNames.some((pName) =>
      userRole.permissions.some((p: { name: string }) => p.name === pName),
    );
  };

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = (permissionNames: string[]): boolean => {
    if (!user.value) return false;

    const rolesStore = useRolesStore();
    const userRole = rolesStore.getRoleById(user.value.role_id);
    if (!userRole) return false;

    return permissionNames.every((pName) =>
      userRole.permissions.some((p: { name: string }) => p.name === pName),
    );
  };

  /**
   * Check if user has specific role
   */
  const hasRole = (roleName: string): boolean => {
    if (!user.value) return false;

    const rolesStore = useRolesStore();
    const userRole = rolesStore.getRoleById(user.value.role_id);
    return userRole?.name === roleName;
  };

  // Actions
  const login = async (username: string, password: string): Promise<void> => {
    isLoading.value = true;

    try {
      const response = await api.post<LoginResponse>("/api/auth/login", {
        username,
        password,
      });

      token.value = response.token;
      user.value = response.user;
      localStorage.setItem("token", response.token);

      // Load roles IMMEDIATELY after login (needed for permission checks)
      const rolesStore = useRolesStore();
      await rolesStore.fetchRoles();

      // Connect WebSocket after successful login
      socketService.connect();

      // Setup auth WebSocket listeners
      setupWebSocketListeners();

      // Setup mappings store listeners after socket connects
      const { useMappingsStore } = await import("./mappings");
      const mappingsStore = useMappingsStore();
      mappingsStore.setupWebSocketListeners();

      // Setup notifications store listeners
      const { useNotificationsStore } = await import("./notifications");
      const notificationsStore = useNotificationsStore();
      notificationsStore.setupWebSocketListeners();

      // Send auth:login event to WebSocket (to join user-specific room)
      socketService.emit(WSEvent.AUTH_LOGIN, {
        userId: response.user.id,
      } as WSEventPayload[typeof WSEvent.AUTH_LOGIN]);
    } catch (error: unknown) {
      // Extract error message from response
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { error?: string } };
        };
        const backendMessage = axiosError.response?.data?.error;
        if (backendMessage) {
          throw new Error(backendMessage);
        }
      }

      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Benutzername oder Passwort ungültig");
    } finally {
      isLoading.value = false;
      isInitializing.value = false;
    }
  };

  const logout = (): void => {
    if (user.value?.id) {
      // Send auth:logout event to WebSocket (to leave user-specific room)
      socketService.emit(WSEvent.AUTH_LOGOUT, {
        userId: user.value.id,
      } as WSEventPayload[typeof WSEvent.AUTH_LOGOUT]);
    }

    user.value = null;
    token.value = null;
    localStorage.removeItem("token");

    // Disconnect WebSocket on logout
    socketService.disconnect();

    // Ensure router guard does not remain blocked in init state
    isInitializing.value = false;
  };

  const fetchCurrentUser = async (): Promise<void> => {
    if (!token.value) {
      isInitializing.value = false;
      return;
    }

    try {
      const response = await api.get<SafeUser>("/api/auth/me");
      user.value = response;
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } } | null)
        ?.response?.status;
      if (status === 401) {
        logout();
        return;
      }

      // Network failures, rate limits and server errors do not invalidate a session.
      // Let the caller offer a retry without discarding the existing credentials.
      throw error;
    } finally {
      isInitializing.value = false;
    }
  };

  /**
   * Setup WebSocket listeners for user updates
   */
  let listenersSetup = false;
  const setupWebSocketListeners = (): void => {
    if (!socketService.socket) return;
    if (listenersSetup) return; // Prevent duplicate listeners

    // Listen for user updates (when admin changes role, etc.)
    socketService.socket.on(
      WSEvent.USER_UPDATED,
      (payload: WSEventPayload[typeof WSEvent.USER_UPDATED]) => {
        if (user.value && payload.user.id === user.value.id) {
          // Update current user data
          user.value = payload.user;
        }
      },
    );

    listenersSetup = true;
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string,
  ): Promise<void> => {
    isLoading.value = true;

    try {
      await api.post("/api/auth/change-password", {
        currentPassword,
        newPassword,
      });
    } catch (error: unknown) {
      // Extract backend error message from axios response
      const axiosError = error as { response?: { data?: { error?: string } } };
      const backendMessage = axiosError.response?.data?.error;

      if (backendMessage) {
        throw new Error(backendMessage);
      } else if (error instanceof Error) {
        throw new Error(`Password change failed: ${error.message}`);
      }
      throw new Error("Password change failed");
    } finally {
      isLoading.value = false;
    }
  };

  return {
    user,
    token,
    isLoading,
    isInitializing,
    isAuthenticated,
    isAdmin,
    canAccessAdmin,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    login,
    logout,
    fetchCurrentUser,
    changePassword,
    setupWebSocketListeners,
  };
});
